// screens/Clark/answer/revealPace.mjs — RUN TA · STEP 1. The reveal's pacing, as pure functions.
//
// 🔴 `.mjs`, AND FOR THE REASON `researchRoute.mjs` IS `.mjs`. `answerApi.js` is ESM inside a
// `"type":"commonjs"` package, which node cannot import — that file's own header says so, and says
// what it cost: *"which is why the single most consequential branch in Clark had no test."* Timing
// code whose whole contract is "how long does this take and what does it show at t" is exactly the
// kind that must be driven in a test rather than reasoned about, so it lives here and `answerApi.js`
// re-exports it. Nothing in this file touches React, the DOM, or a timer.
//
// The WHY — why the reveal is client-side over a `done` payload rather than a streamed one — is in
// `answerApi.js` beside the re-export, with the transport it is a decision about.

/** 🔴 THE ONE TUNABLE. Everything else here is derived from it or is a bound on it. */
export const REVEAL_CPS = 600;
/** Floor: below this a short answer flickers rather than reveals. */
export const REVEAL_MIN_MS = 900;
/** Ceiling: 🔴 "never a crawl". A 12,000-character answer still finishes in five seconds. */
export const REVEAL_MAX_MS = 5000;

/* What an `unsupported` segment RENDERS, which is what the reveal is pacing. Its `text` is absent
   by construction (clarkAnswerV2.js keeps the withheld words off the wire), so its length cannot be
   read off the segment and must be stated.
   🔴 RUN CR · BLOCK 6 — it renders an ellipsis now, not the words `[quotation withheld]`, and this
   constant moved with it. A weight of 20 for a mark one character wide would pace the reveal around
   text that is no longer on screen. The segment only occurs on LEGACY stored envelopes at all; see
   ClarkAnswerV2.jsx's branch. */
const UNSUPPORTED_CHARS = "…".length;

/** The number of characters a segment puts on screen. */
export function revealWeight(seg) {
  if (!seg) return 0;
  if (seg.type === "unsupported") return UNSUPPORTED_CHARS;
  return String(seg.text ?? "").length;
}

/** Total visible characters in an answer's segment list. */
export function revealTotal(segments) {
  return (segments || []).reduce((n, s) => n + revealWeight(s), 0);
}

/** How long the whole reveal should take, from its length. One constant, two bounds. */
export function revealDurationMs(total, cps = REVEAL_CPS) {
  const ms = (Number(total) || 0) / cps * 1000;
  return Math.max(REVEAL_MIN_MS, Math.min(REVEAL_MAX_MS, ms));
}

/**
 * The prefix of `segments` covering `shown` visible characters.
 *
 * 🔴 MARKER SEGMENTS ARE ATOMIC. `unresolved` / `unchecked` / `unsupported` render as a `<mark>` or
 * a bracketed phrase with a tooltip and a screen-reader line; revealing one letter at a time would
 * paint a half-built badge, and — worse — would put the first three characters of a case name we
 * are about to say we could NOT find on screen with no marker around them yet. They appear whole
 * or not at all. An unknown future type falls here too and is treated as atomic, which is the
 * conservative direction (the same default `toParagraphs` and the render switch already take).
 *
 * 🔴 TEXT IS CUT AT A WORD BOUNDARY, never mid-word. A cursor that lands inside "jurisdiction"
 * paints "jurisdi" for one frame, and a word tearing and re-forming sixty times a second is the
 * thing that makes a reveal feel broken rather than alive.
 */
export function revealSegments(segments, shown) {
  const segs = segments || [];
  if (!(shown >= 0)) return segs;
  const out = [];
  let acc = 0;
  for (const s of segs) {
    const w = revealWeight(s);
    if (acc + w <= shown) { out.push(s); acc += w; continue; }
    // Partial. Atomic types stop the walk rather than render half of themselves.
    if (s.type && s.type !== "text") break;
    const room = shown - acc;
    if (room <= 0) break;
    const text = String(s.text ?? "");
    let cut = room;
    // Back off to the last whitespace at or before the cursor. If the current word started before
    // the cursor and has not finished, it waits — at 600 c/s that is under 30 ms for a long word.
    while (cut > 0 && !/\s/.test(text[cut])) cut -= 1;
    if (cut <= 0) break;
    out.push({ ...s, text: text.slice(0, cut) });
    break;
  }
  return out;
}

/* ⚠️ THERE WAS A CANCEL REGISTRY HERE, AND ITS REMOVAL IS THE FINDING, NOT THE TIDY-UP.
   The re-send interrupt was first built as `registerReveal(snap)` here plus one `cancelReveals()`
   call in `index.jsx`'s `send()`. It worked. It was withdrawn because `index.jsx` turned out to
   carry RUN TC's UNCOMMITTED rebuild of the grounded predicate inside that same function, so the
   one line could only have shipped by committing a sibling's unfinished work. The interrupt now
   lives in `ClarkAnswerV2.jsx` as a `MutationObserver` on the turn list and needs nothing from
   here — so the registry is deleted rather than left exported with no caller. */
